import React, { createContext, useContext, useEffect, useState } from 'react';

type FontSize = 'small' | 'medium' | 'large' | 'extra-large';

interface AccessibilitySettings {
  fontSize: FontSize;
  reducedMotion: boolean;
  highContrast: boolean;
  screenReaderOptimized: boolean;
  differentiateWithoutColor: boolean;
  captionsEnabled: boolean;
  audioDescriptions: boolean;
}

interface AccessibilityContextType extends AccessibilitySettings {
  setFontSize: (size: FontSize) => void;
  setReducedMotion: (enabled: boolean) => void;
  setHighContrast: (enabled: boolean) => void;
  setScreenReaderOptimized: (enabled: boolean) => void;
  setDifferentiateWithoutColor: (enabled: boolean) => void;
  setCaptionsEnabled: (enabled: boolean) => void;
  setAudioDescriptions: (enabled: boolean) => void;
  resetToDefaults: () => void;
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

const ACCESSIBILITY_STORAGE_KEY = 'wickedcrm-accessibility';

const defaultSettings: AccessibilitySettings = {
  fontSize: 'medium',
  reducedMotion: false,
  highContrast: false,
  screenReaderOptimized: false,
  differentiateWithoutColor: false,
  captionsEnabled: false,
  audioDescriptions: false,
};

const fontSizeMap: Record<FontSize, string> = {
  'small': '14px',
  'medium': '16px',
  'large': '18px',
  'extra-large': '20px',
};

export function AccessibilityProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(ACCESSIBILITY_STORAGE_KEY);
      if (stored) {
        try {
          return { ...defaultSettings, ...JSON.parse(stored) };
        } catch {
          return defaultSettings;
        }
      }
      // Check system preferences
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const prefersContrast = window.matchMedia('(prefers-contrast: more)').matches;
      if (prefersReducedMotion || prefersContrast) {
        return {
          ...defaultSettings,
          reducedMotion: prefersReducedMotion,
          highContrast: prefersContrast,
        };
      }
    }
    return defaultSettings;
  });

  useEffect(() => {
    const root = window.document.documentElement;

    // Apply font size with iOS Dynamic Type support
    root.style.fontSize = fontSizeMap[settings.fontSize];

    // Toggle CSS classes
    const classToggles: [string, boolean][] = [
      ['reduce-motion', settings.reducedMotion],
      ['high-contrast', settings.highContrast],
      ['sr-optimized', settings.screenReaderOptimized],
      ['differentiate-color', settings.differentiateWithoutColor],
      ['captions-on', settings.captionsEnabled],
      ['audio-desc-on', settings.audioDescriptions],
    ];

    for (const [cls, enabled] of classToggles) {
      root.classList.toggle(cls, enabled);
    }

    // Save to localStorage
    localStorage.setItem(ACCESSIBILITY_STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  // Listen for system preference changes
  useEffect(() => {
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const contrastQuery = window.matchMedia('(prefers-contrast: more)');

    const handleMotionChange = (e: MediaQueryListEvent) => {
      setSettings(prev => {
        const stored = localStorage.getItem(ACCESSIBILITY_STORAGE_KEY);
        if (stored) return prev; // User has explicitly set preferences
        return { ...prev, reducedMotion: e.matches };
      });
    };

    const handleContrastChange = (e: MediaQueryListEvent) => {
      setSettings(prev => {
        const stored = localStorage.getItem(ACCESSIBILITY_STORAGE_KEY);
        if (stored) return prev;
        return { ...prev, highContrast: e.matches };
      });
    };

    motionQuery.addEventListener('change', handleMotionChange);
    contrastQuery.addEventListener('change', handleContrastChange);
    return () => {
      motionQuery.removeEventListener('change', handleMotionChange);
      contrastQuery.removeEventListener('change', handleContrastChange);
    };
  }, []);

  const setFontSize = (size: FontSize) => setSettings(prev => ({ ...prev, fontSize: size }));
  const setReducedMotion = (enabled: boolean) => setSettings(prev => ({ ...prev, reducedMotion: enabled }));
  const setHighContrast = (enabled: boolean) => setSettings(prev => ({ ...prev, highContrast: enabled }));
  const setScreenReaderOptimized = (enabled: boolean) => setSettings(prev => ({ ...prev, screenReaderOptimized: enabled }));
  const setDifferentiateWithoutColor = (enabled: boolean) => setSettings(prev => ({ ...prev, differentiateWithoutColor: enabled }));
  const setCaptionsEnabled = (enabled: boolean) => setSettings(prev => ({ ...prev, captionsEnabled: enabled }));
  const setAudioDescriptions = (enabled: boolean) => setSettings(prev => ({ ...prev, audioDescriptions: enabled }));
  const resetToDefaults = () => setSettings(defaultSettings);

  return (
    <AccessibilityContext.Provider
      value={{
        ...settings,
        setFontSize,
        setReducedMotion,
        setHighContrast,
        setScreenReaderOptimized,
        setDifferentiateWithoutColor,
        setCaptionsEnabled,
        setAudioDescriptions,
        resetToDefaults,
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
}
